import { api } from '../api.js';
import { refreshContractorBadge } from '../components/header.js';

let _container = null;
let _state = {
  rows: [],
  status: 'pending_approval',
  heartbeats: [],
};

export async function initContractorUpdates(container) {
  _container = container;
  _state = { rows: [], status: 'pending_approval', heartbeats: [] };

  container.innerHTML = '<div class="loading-spinner">Загрузка контрактор-апдейтов...</div>';

  try {
    await Promise.all([loadUpdates(), loadHeartbeats()]);
  } catch (e) {
    container.innerHTML = `<div class="loading-spinner" style="color:var(--text-danger)">
      ${e.message}
    </div>`;
    return;
  }
  render();
}

async function loadUpdates() {
  _state.rows = await api.contractorUpdates(_state.status, 100, 0);
}

async function loadHeartbeats() {
  _state.heartbeats = await api.cronHeartbeats();
}

function render() {
  _container.innerHTML = `
    <div class="cu-root">
      <h2 style="margin-bottom: var(--space-3); font-size: var(--text-xl);">
        🤝 Контрактор-апдейты
      </h2>

      ${renderHeartbeats()}

      <div class="cu-toolbar">
        <label>Статус
          <select id="cu-status-select">
            <option value="pending_approval" ${_state.status === 'pending_approval' ? 'selected' : ''}>
              Ждут одобрения
            </option>
            <option value="processed" ${_state.status === 'processed' ? 'selected' : ''}>
              Обработанные
            </option>
            <option value="all" ${_state.status === 'all' ? 'selected' : ''}>
              Все
            </option>
          </select>
        </label>
        <button id="cu-refresh">Обновить</button>
      </div>

      <div class="cu-list">
        ${renderRows()}
      </div>
    </div>
  `;
  attachEvents();
}

function renderHeartbeats() {
  if (!_state.heartbeats.length) {
    return '<div class="cu-heartbeats cu-heartbeats--empty">Cron heartbeats: пусто (ни один cron-job ещё не запускался)</div>';
  }
  const items = _state.heartbeats.map(h => {
    const cls = healthClass(h);
    const ageText = formatAge(h.seconds_since_run);
    return `<span class="cu-hb-pill ${cls}" title="${escHtml(h.last_error ?? '')}">
      ${escHtml(h.job_name)}: ${escHtml(h.last_status ?? 'unknown')} (${ageText})
    </span>`;
  }).join('');
  return `<div class="cu-heartbeats">Cron: ${items}</div>`;
}

function healthClass(h) {
  const sec = h.seconds_since_run ?? 99999;
  const status = (h.last_status ?? '').toLowerCase();
  if (status === 'error' || sec > 3600) return 'cu-hb-red';
  if (sec > 900) return 'cu-hb-yellow';
  return 'cu-hb-green';
}

function formatAge(sec) {
  if (sec == null) return '?';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function renderRows() {
  if (!_state.rows.length) {
    return '<div class="cu-empty">Нет апдейтов в этой группе</div>';
  }
  return _state.rows.map(renderRow).join('');
}

function renderRow(r) {
  const ts = formatTime(r.created_at);
  const isPending = r.requires_approval && !r.approved_by && !r.processed;
  const reason = r.result?.reason ?? '';
  const taskInfo = r.task_id
    ? `задача №${r.task_id} «${escHtml(r.task_name ?? '')}» (проект ${escHtml(r.project_name ?? '?')})`
    : 'задача не определена';

  return `
    <div class="cu-row" data-id="${r.id}">
      <div class="cu-row-head">
        <span class="cu-row-time">${escHtml(ts)}</span>
        <span class="cu-row-contractor">${escHtml(r.contractor_name ?? '')} (${escHtml(r.contractor_role ?? '')})</span>
        <span class="cu-row-channel">[${escHtml(r.channel ?? '')}]</span>
        ${isPending ? '<span class="cu-row-badge">требует одобрения</span>' : ''}
        ${r.processed ? '<span class="cu-row-badge cu-row-badge--done">обработан</span>' : ''}
      </div>
      <div class="cu-row-task">${taskInfo}</div>
      <div class="cu-row-message">${escHtml(r.raw_message ?? '')}</div>
      ${reason ? `<div class="cu-row-reason">Причина: <code>${escHtml(reason)}</code></div>` : ''}
      ${isPending ? `
        <div class="cu-row-actions">
          <button class="btn-primary cu-approve" data-id="${r.id}">✓ Одобрить</button>
          <button class="btn-danger cu-reject" data-id="${r.id}">✗ Отклонить</button>
        </div>
      ` : ''}
    </div>
  `;
}

function attachEvents() {
  _container.querySelector('#cu-refresh')?.addEventListener('click', async () => {
    await Promise.all([loadUpdates(), loadHeartbeats()]);
    render();
  });

  _container.querySelector('#cu-status-select')?.addEventListener('change', async (e) => {
    _state.status = e.target.value;
    await loadUpdates();
    render();
  });

  _container.querySelectorAll('.cu-approve').forEach(btn => {
    btn.addEventListener('click', () => onApprove(Number(btn.dataset.id)));
  });
  _container.querySelectorAll('.cu-reject').forEach(btn => {
    btn.addEventListener('click', () => onReject(Number(btn.dataset.id)));
  });
}

async function onApprove(id) {
  const note = prompt('Комментарий к одобрению (опционально):', '');
  if (note === null) return;  // cancelled
  try {
    await api.approveContractorUpdate(id, { note: note || null });
    await loadUpdates();
    refreshContractorBadge();
    render();
  } catch (e) {
    alert(`Не удалось одобрить: ${e.message}`);
  }
}

async function onReject(id) {
  const reason = prompt('Причина отклонения (увидит подрядчик):', '');
  if (!reason || !reason.trim()) return;
  try {
    await api.rejectContractorUpdate(id, { reason: reason.trim() });
    await loadUpdates();
    refreshContractorBadge();
    render();
  } catch (e) {
    alert(`Не удалось отклонить: ${e.message}`);
  }
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function escHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
